// Historial de órdenes de cotización: una sola tabla, sin tabs. Lenguaje B2B (orden de compra).
// Columna Estado con tag; "Requiere aprobación" cuando cotizada; acción clara (Continuar orden / Ver orden).

import { useCallback, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useClientLead, useClientQuotationRequests, useSourcingItems, useBrands, useOrigins, useSourcingItemFamilies, useProductFamilies } from '@/hooks/use-store';
import { useQuotationCart } from '@/hooks/use-quotation-cart';
import { postWorkerApi, postWorkerApiWithAuth } from '@/lib/worker-api';
import { supabase } from '@/lib/supabase';
import { StoreBreadcrumbs } from '@/components/store/StoreBreadcrumbs';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useToast } from '@/hooks/use-toast';
import { PlusCircle, ArrowRightCircle, AlertCircle, FileText, Copy, Loader2 } from 'lucide-react';
import type { QuotationRequest, QuotationRequestStatus } from '@/lib/store-types';
import type { QuotationDocBlocks, MixBlock, MixBlockRow } from '@/lib/store-types';
import { isFullContainerRow } from '@/pages/store/store-cart-sections';

type ReqWithDoc = QuotationRequest & { approved_doc?: { id: string; approved_pdf_url?: string; download_expires_at?: string } };

/** Número único de orden de cotización (últimos 8 caracteres del id en mayúsculas). */
function orderNumber(id: string): string {
  return id.slice(-8).toUpperCase();
}

/** Lookups to build quotation summary (families, brands, origin per item). */
export interface QuotationSummaryLookups {
  itemIdToFamilyNames: Map<string, string[]>;
  itemIdToBrandName: Map<string, string>;
  itemIdToOriginName: Map<string, string>;
}

/**
 * Build summary lines for a quotation from its blocks.
 * Format per line: "N x Contenedor Mixto|Completo . {Familia1, Familia2} . {Marca1, Marca2} . {origen}"
 */
function buildQuotationSummaryLines(
  blocks: QuotationDocBlocks | undefined,
  lookups: QuotationSummaryLookups
): string[] {
  const mix = blocks?.mix ?? [];
  if (mix.length === 0) return [];
  const lines: string[] = [];
  for (const block of mix as MixBlock[]) {
    const rows = (block.rows ?? []) as MixBlockRow[];
    if (rows.length === 0) continue;
    const isFull = rows.length === 1 && isFullContainerRow(rows[0]);
    const n = block.num_containers ?? 1;
    const containerLabel = isFull ? 'Contenedor Completo' : 'Contenedor Mixto';
    const families = new Set<string>();
    const brands = new Set<string>();
    const origins = new Set<string>();
    for (const row of rows) {
      const id = row.sourcing_item_id;
      const fams = lookups.itemIdToFamilyNames.get(id) ?? [];
      fams.forEach((f) => families.add(f));
      const brand = lookups.itemIdToBrandName.get(id);
      if (brand) brands.add(brand);
      const origin = lookups.itemIdToOriginName.get(id);
      if (origin) origins.add(origin);
    }
    const familyStr = [...families].filter(Boolean).join(', ') || '—';
    const brandStr = [...brands].filter(Boolean).join(', ') || '—';
    const originStr = [...origins].filter(Boolean).join(', ') || '—';
    lines.push(`${n} × ${containerLabel} . ${familyStr} . ${brandStr} . ${originStr}`);
  }
  return lines;
}

/** Etiquetas de estado alineadas con orden B2B (columna Estado). */
const STATUS_TAG: Record<string, { label: string; variant: 'secondary' | 'default' | 'outline' }> = {
  draft: { label: 'Sin enviar', variant: 'secondary' },
  pending: { label: 'Enviada', variant: 'secondary' },
  en_revision: { label: 'En revisión', variant: 'default' },
  cotizado: { label: 'Lista para aprobar', variant: 'outline' },
  finished: { label: 'Cerrada', variant: 'secondary' },
  pending_deposit_payment: { label: 'Pendiente pago depósito', variant: 'outline' },
  in_process: { label: 'En proceso', variant: 'default' },
  shipped: { label: 'En camino y Pend. pago balance', variant: 'default' },
  pending_final_payment: { label: 'En camino y Pend. pago balance', variant: 'default' },
  arriving_soon: { label: 'En camino', variant: 'default' },
  delivered: { label: 'Entregado', variant: 'secondary' },
};

/** Indica si el cliente debe hacer algo y qué (para columna "Requiere acción"). */
function clientActionHint(status: QuotationRequestStatus): string | null {
  switch (status) {
    case 'cotizado':
      return 'Aprobar o pedir cambios';
    case 'pending_deposit_payment':
      return 'Subir comprobante de depósito';
    case 'shipped':
    case 'pending_final_payment':
      return 'Subir comprobante de pago balance';
    default:
      return null;
  }
}

function formatDate(createdAt: string): string {
  return new Date(createdAt).toLocaleDateString('es-ES', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function summaryText(req: ReqWithDoc): string {
  const items = Array.isArray(req.items) ? req.items : [];
  const count = items.reduce((acc: number, i: { quantity?: number }) => acc + (i.quantity ?? 0), 0);
  return `${items.length} producto(s) · ${count.toLocaleString()} uds`;
}

const StoreQuotationHistory = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { setCurrentRequestId } = useQuotationCart();
  const { data: lead, isLoading: leadLoading } = useClientLead();
  const { data: requests, isLoading: reqLoading } = useClientQuotationRequests(lead?.id ?? null);
  const [creatingNew, setCreatingNew] = useState(false);
  const [duplicatePendingId, setDuplicatePendingId] = useState<string | null>(null);
  const getSession = useCallback(() => supabase.auth.getSession(), []);
  const { data: allItems } = useSourcingItems(null);
  const { data: brands } = useBrands();
  const { data: origins } = useOrigins();
  const { data: itemFamilies } = useSourcingItemFamilies();
  const { data: productFamilies } = useProductFamilies(null);

  const summaryLookups = useMemo((): QuotationSummaryLookups => {
    const itemIdToFamilyNames = new Map<string, string[]>();
    const familyNamesById = new Map((productFamilies ?? []).map((f) => [f.id, f.family_name]));
    for (const j of itemFamilies ?? []) {
      const name = familyNamesById.get(j.family_id);
      if (!name) continue;
      const list = itemIdToFamilyNames.get(j.item_id) ?? [];
      if (!list.includes(name)) list.push(name);
      itemIdToFamilyNames.set(j.item_id, list);
    }
    const brandNamesById = new Map((brands ?? []).map((b) => [b.id, b.name]));
    const originNamesById = new Map((origins ?? []).map((o) => [o.id, o.name]));
    const itemIdToBrandName = new Map<string, string>();
    const itemIdToOriginName = new Map<string, string>();
    for (const item of allItems ?? []) {
      if (item.brand_id) itemIdToBrandName.set(item.id, brandNamesById.get(item.brand_id) ?? '');
      if (item.origin_id) itemIdToOriginName.set(item.id, originNamesById.get(item.origin_id) ?? '');
    }
    return { itemIdToFamilyNames, itemIdToBrandName, itemIdToOriginName };
  }, [allItems, brands, origins, itemFamilies, productFamilies]);

  const isLoading = leadLoading || reqLoading;
  /** Todas las órdenes ordenadas por fecha (más reciente primero). Una sola tabla sin tabs. */
  const sortedRequests = useMemo(() => {
    const list = (requests ?? []) as ReqWithDoc[];
    return [...list].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
  }, [requests]);

  const handleContinueDraft = (id: string) => {
    setCurrentRequestId(id);
    navigate('/store/cart');
  };

  const handleNewDraft = async () => {
    if (!lead?.id) return;
    setCreatingNew(true);
    try {
      const res = await postWorkerApi<{ request_id: string }>('/api/quotation-request/ensure', {
        lead_id: lead.id,
        create_new: true,
      });
      if (!res.ok || !res.data?.request_id) {
        toast({ title: 'Error', description: 'No se pudo crear el borrador', variant: 'destructive' });
        return;
      }
      setCurrentRequestId(res.data.request_id);
      navigate('/store/cart');
    } catch {
      toast({ title: 'Error', description: 'No se pudo crear el borrador', variant: 'destructive' });
    } finally {
      setCreatingNew(false);
    }
  };

  const handleViewQuotation = (id: string) => {
    navigate(`/store/quotations/${encodeURIComponent(id)}`);
  };

  const handleDuplicate = useCallback(
    async (sourceRequestId: string) => {
      setDuplicatePendingId(sourceRequestId);
      try {
        const res = await postWorkerApiWithAuth<{ request_id?: string }>(
          '/api/quotation-request/duplicate',
          { source_request_id: sourceRequestId },
          getSession
        );
        if (res.ok && res.data?.request_id) {
          setCurrentRequestId(res.data.request_id);
          toast({ title: 'Solicitud duplicada', description: 'Se ha creado una copia con el mismo estado.' });
          navigate(`/store/quotations/${res.data.request_id}`);
        } else {
          toast({ title: res.error ?? 'Error al duplicar', variant: 'destructive' });
        }
      } catch {
        toast({ title: 'Error al duplicar la solicitud', variant: 'destructive' });
      } finally {
        setDuplicatePendingId(null);
      }
    },
    [getSession, setCurrentRequestId, navigate, toast]
  );

  const breadcrumbs = [
    { label: 'Store', path: '/store' },
    { label: 'Mis órdenes' },
  ];

  if (!lead && !isLoading) {
    return (
      <div className="space-y-4">
        <StoreBreadcrumbs segments={breadcrumbs} />
        <p className="text-zinc-600">Debe iniciar sesión para ver su historial.</p>
        <Button asChild variant="outline">
          <Link to="/store/login">Iniciar sesión</Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <StoreBreadcrumbs segments={breadcrumbs} />
      <header className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-zinc-900 tracking-tight">Mis órdenes de cotización</h1>
          <p className="text-sm text-zinc-600 max-w-2xl mt-1">
            Abre una orden para ver el detalle, continuar editando (si no está enviada) o aprobar la cotización cuando esté lista.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={handleNewDraft} disabled={creatingNew} className="gap-1.5 shrink-0">
          <PlusCircle className="h-4 w-4" />
          {creatingNew ? 'Creando...' : 'Nueva orden'}
        </Button>
      </header>

      {isLoading ? (
        <div className="rounded-xl border border-zinc-200 bg-zinc-50/50 p-8 text-center">
          <p className="text-zinc-600">Cargando órdenes...</p>
        </div>
      ) : sortedRequests.length === 0 ? (
        <div className="rounded-xl border-2 border-dashed border-zinc-300 bg-zinc-50/80 p-10 text-center">
          <FileText className="h-10 w-10 text-zinc-400 mx-auto mb-3" />
          <p className="text-zinc-600 font-medium">No tienes órdenes</p>
          <p className="text-xs text-zinc-500 mt-1">Crea una desde el carrito o con el botón Nueva orden.</p>
          <Button variant="outline" className="mt-4 gap-1.5" onClick={handleNewDraft} disabled={creatingNew}>
            <PlusCircle className="h-4 w-4" />
            Nueva orden
          </Button>
        </div>
      ) : (
        <div className="rounded-lg border border-zinc-200 overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-zinc-50/80">
                <TableHead className="font-semibold text-zinc-700">Orden</TableHead>
                <TableHead className="font-semibold text-zinc-700">Fecha</TableHead>
                <TableHead className="font-semibold text-zinc-700">Estado</TableHead>
                <TableHead className="font-semibold text-zinc-700">Resumen</TableHead>
                <TableHead className="font-semibold text-zinc-700 w-[180px]">Requiere acción</TableHead>
                <TableHead className="font-semibold text-zinc-700 text-right w-[1%] whitespace-nowrap">Acción</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedRequests.map((req) => {
                const status = (req.status ?? 'draft') as QuotationRequestStatus;
                const tag = STATUS_TAG[status] ?? STATUS_TAG.draft;
                const actionHint = clientActionHint(status);
                const isDraft = status === 'draft';
                return (
                  <TableRow
                    key={req.id}
                    className={actionHint ? 'bg-amber-50/60 border-l-4 border-l-amber-400' : undefined}
                  >
                    <TableCell className="font-mono text-zinc-900" title={req.id}>
                      #{orderNumber(req.id)}
                    </TableCell>
                    <TableCell className="text-zinc-600">{formatDate(req.created_at)}</TableCell>
                    <TableCell>
                      <Badge variant={tag.variant} className="text-[10px]">
                        {tag.label}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-zinc-600 max-w-[200px] truncate" title={buildQuotationSummaryLines(req.blocks, summaryLookups).join('\n')}>
                      {summaryText(req)}
                    </TableCell>
                    <TableCell>
                      {actionHint ? (
                        <span className="inline-flex items-center gap-1 text-amber-700 text-xs font-medium">
                          <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                          {actionHint}
                        </span>
                      ) : (
                        <span className="text-zinc-400 text-xs">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      <div className="flex items-center justify-end gap-2">
                        {isDraft ? (
                          <Button size="sm" variant="outline" className="gap-1.5" onClick={() => handleContinueDraft(req.id)}>
                            <ArrowRightCircle className="h-3.5 w-3.5" />
                            Continuar orden
                          </Button>
                        ) : (
                          <Button size="sm" variant={actionHint ? 'default' : 'outline'} className="gap-1.5" asChild>
                            <Link to={`/store/quotations/${req.id}`}>
                              <ArrowRightCircle className="h-3.5 w-3.5" />
                              Ver orden
                            </Link>
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          className="gap-1.5 text-zinc-600 hover:text-zinc-900"
                          disabled={duplicatePendingId !== null}
                          onClick={() => handleDuplicate(req.id)}
                        >
                          {duplicatePendingId === req.id ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : (
                            <Copy className="h-3.5 w-3.5" />
                          )}
                          {duplicatePendingId === req.id ? 'Creando…' : 'Duplicar'}
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
};

export default StoreQuotationHistory;
